import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { motion } from "framer-motion";
import ContactUs from "./ContactUs";

// Media Assets
import bottledWaterImage from "../media/bottledWaterImage.jpg";
import RO from "../media/Reverse Osmosis.jpg";
import Osmosis from "../media/Osmosis.jpg";
import Ozonation from "../media/Ozonation.jpg";
import MicroFiltration from "../media/Micro-Filtration.png";
import CarbonFilter from "../media/Carbon Filtration.png";
import SandFilter from "../media/Carbon Filtration.png";
import UV from "../media/Ultra-Violet Treatment.png";

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "About NGO Waters | 50% Profit for Charity Model";
  }, []);

  const purificationSteps = [
    { step: 1, imgUrl: UV, name: "Ultra-Violet (UV) Treatment" },
    { step: 2, imgUrl: RO, name: "Reverse Osmosis (RO)" },
    { step: 3, imgUrl: CarbonFilter, name: "Carbon Filtration" },
    { step: 4, imgUrl: SandFilter, name: "Sand Filtration" },
    { step: 5, imgUrl: MicroFiltration, name: "Micro-Filtration" },
    { step: 6, imgUrl: Osmosis, name: "Osmosis" },
    { step: 7, imgUrl: Ozonation, name: "Ozonation" },
  ];

  const benefits = [
    {
      icon: "bi-heart-fill",
      styleColor: "#dc3545",
      title: "50% Profit to Charity",
      text: "Every commercial framework purchase directly funds community sanitation systems, deep fresh water pipeline installations, and local relief distribution protocols.",
    },
    {
      icon: "bi-patch-check-fill",
      styleColor: "var(--brand-mineral-aqua)",
      title: "7-Stage Scientific Pure Lab",
      text: "Engineered passing continuous quality constraints including custom reverse mechanics, micron separation, and certified chemical treatment systems.",
    },
    {
      icon: "bi-lightning-fill",
      styleColor: "var(--brand-mineral-aqua)",
      title: "Massive Automated Supply",
      text: "Utilizing professional industrial production grids to seamlessly support scalable hydration access over high-capacity local pipelines.",
    },
    {
      icon: "bi-currency-rupee",
      styleColor: "var(--brand-copper)",
      title: "Balanced Fair Ecosystem",
      text: "India's pioneer enterprise pipeline structure that securely coordinates commercial high-volume logistics alongside transparent humanitarian aid blueprints.",
    },
  ];

  const fluidSpring = { type: "spring", stiffness: 50, damping: 16 };
  const springConfig = { type: "spring", stiffness: 300, damping: 20 };

  return (
    <motion.div
      className="container-fluid min-vh-100 position-relative overflow-hidden py-5 px-3 px-md-5 bg-light"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Low-Opacity Ambient Soft Blobs */}
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "500px",
          height: "500px",
          background: "rgba(54, 191, 216, 0.03)",
          top: "-150px",
          right: "-50px",
          filter: "blur(120px)",
          pointerEvents: "none",
        }}
      />
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "400px",
          height: "400px",
          background: "rgba(166, 123, 91, 0.02)",
          bottom: "15%",
          left: "-80px",
          filter: "blur(100px)",
          pointerEvents: "none",
        }}
      />

      {/* ================= HERO SECTION ================= */}
      <section className="container py-4 position-relative z-1 mb-5">
        <div className="row align-items-center g-5">
          <motion.div
            className="col-lg-7 text-center text-lg-start"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <span
              className="badge px-3 py-2 mb-3 text-uppercase fw-semibold tracking-wider"
              style={{
                backgroundColor: "rgba(54, 191, 216, 0.1)",
                color: "var(--brand-deep-blue)",
                fontSize: "0.75rem",
                borderRadius: "8px",
              }}
            >
              Born for Charity, Built for Impact
            </span>
            <h1
              className="display-5 fw-bold mb-4"
              style={{
                color: "var(--brand-deep-blue)",
                letterSpacing: "-1.5px",
                lineHeight: "1.2",
              }}
            >
              Pure Minerals.
              <br />
              <span style={{ color: "var(--brand-mineral-aqua)" }}>
                Real Change.
              </span>
            </h1>
            <p
              className="text-secondary fs-5 mb-4"
              style={{ lineHeight: "1.7", fontWeight: "400" }}
            >
              NGO Waters™ stands as India's first dedicated utility-driven
              packaging ecosystem that matches daily healthy hydration needs
              with immediate social action. We distribute crisp, premium mineral
              water while consistently anchoring{" "}
              <strong style={{ color: "var(--brand-deep-blue)" }}>
                50% of our net profits
              </strong>{" "}
              toward community water security.
            </p>
            <p className="text-muted small mb-4" style={{ lineHeight: "1.6" }}>
              With manufacturing resources driven by{" "}
              <strong>Ponia Food And Beverage Private Limited</strong> (CIN:
              U11043UP2024PTC213517) based out of Aligarh, Uttar Pradesh, we
              maintain structural supply pipelines that cleanly serve bulk
              commercial operations.
            </p>
            <div className="d-flex flex-wrap justify-content-center justify-content-lg-start gap-3 pt-2">
              <motion.a
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={springConfig}
                href="https://www.cognitoforms.com/HARIOMSINGH1/NGOWatersBulkOrderRequest"
                target="_blank"
                rel="noopener noreferrer"
                className="btn text-white px-4 py-2.5 fw-bold border-0 d-flex align-items-center gap-2"
                style={{
                  backgroundColor: "var(--brand-deep-blue)",
                  borderRadius: "12px",
                  fontSize: "0.95rem",
                }}
              >
                <i className="bi bi-cart-fill"></i> Order in Bulk
              </motion.a>
              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={springConfig}
              >
                <Link
                  to="/contact"
                  className="btn btn-outline-dark px-4 py-2.5 fw-semibold"
                  style={{ borderRadius: "12px", fontSize: "0.95rem" }}
                >
                  Partner With Us
                </Link>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            className="col-lg-5 text-center"
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, type: "spring" }}
          >
            <div
              className="p-3 bg-white position-relative"
              style={{
                borderRadius: "24px",
                boxShadow: "0 10px 40px rgba(0,0,0,0.02)",
                border: "1px solid rgba(0,0,0,0.03)",
              }}
            >
              <div
                className="position-absolute start-0 top-50 translate-middle-y"
                style={{
                  width: "4px",
                  height: "30%",
                  backgroundColor: "var(--brand-mineral-aqua)",
                  borderRadius: "0 8px 8px 0",
                }}
              />
              <LazyLoadImage
                src={bottledWaterImage}
                alt="NGO Waters premium packaged mineral water bottleneck presentation"
                className="img-fluid w-100"
                style={{ borderRadius: "16px", objectFit: "cover" }}
                effect="blur"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= CORE SYSTEM GRID ================= */}
      <section className="container my-5 position-relative z-1">
        <div className="row g-4">
          <motion.div
            className="col-lg-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div
              className="p-4 p-xl-5 bg-white h-100 border"
              style={{
                borderRadius: "24px",
                borderColor: "rgba(0,0,0,0.04)",
                boxShadow: "0 8px 30px rgba(0,0,0,0.01)",
              }}
            >
              <div className="d-flex align-items-center gap-3 mb-4">
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center"
                  style={{
                    backgroundColor: "rgba(26, 60, 107, 0.05)",
                    width: "42px",
                    height: "42px",
                    minWidth: "42px",
                  }}
                >
                  <i
                    className="bi bi-geo-alt-fill fs-5"
                    style={{ color: "var(--brand-deep-blue)" }}
                  ></i>
                </div>
                <h4
                  className="fw-bold mb-0"
                  style={{
                    color: "var(--brand-deep-blue)",
                    fontSize: "1.25rem",
                  }}
                >
                  Conscious Regional Operations
                </h4>
              </div>
              <p
                className="text-secondary small mb-0"
                style={{ lineHeight: "1.75" }}
              >
                Headquartered strategically in Aligarh, Uttar Pradesh, NGO
                Waters redefines standard industry consumer mechanics. We show
                that complex multi-level logistical scaling can operate
                transparently without prioritizing corporate overheads. Our
                manufacturing tracks systematic purity regulations, assuring
                zero compromises on safety benchmarks.
              </p>
            </div>
          </motion.div>

          <motion.div
            className="col-lg-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.05 }}
          >
            <div
              className="p-4 p-xl-5 bg-white h-100 border"
              style={{
                borderRadius: "24px",
                borderColor: "rgba(0,0,0,0.04)",
                boxShadow: "0 8px 30px rgba(0,0,0,0.01)",
              }}
            >
              <div className="d-flex align-items-center gap-3 mb-4">
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center"
                  style={{
                    backgroundColor: "rgba(54, 191, 216, 0.08)",
                    width: "42px",
                    height: "42px",
                    minWidth: "42px",
                  }}
                >
                  <i
                    className="bi bi-diagram-3-fill fs-5"
                    style={{ color: "var(--brand-mineral-aqua)" }}
                  ></i>
                </div>
                <h4
                  className="fw-bold mb-0"
                  style={{
                    color: "var(--brand-deep-blue)",
                    fontSize: "1.25rem",
                  }}
                >
                  India's First Charity-Revenue Stream
                </h4>
              </div>
              <div className="d-flex flex-column gap-3">
                <div
                  className="p-3 rounded-3 d-flex align-items-start gap-3 border-start border-3 border-danger"
                  style={{
                    backgroundColor: "rgba(220, 53, 69, 0.01)",
                    border: "1px solid rgba(0,0,0,0.02)",
                  }}
                >
                  <i className="bi bi-heart-fill text-danger fs-5 mt-0.5"></i>
                  <span
                    className="small text-secondary"
                    style={{ lineHeight: "1.6" }}
                  >
                    <strong>50% Net Profit Distribution:</strong> Dedicated
                    explicitly toward community water pipeline structures,
                    deep-well conservation boring, and essential welfare setups.
                  </span>
                </div>
                <div
                  className="p-3 rounded-3 d-flex align-items-start gap-3 border-start border-3 border-secondary"
                  style={{
                    backgroundColor: "rgba(0,0,0,0.005)",
                    border: "1px solid rgba(0,0,0,0.02)",
                  }}
                >
                  <i className="bi bi-arrow-repeat text-secondary fs-5 mt-0.5"></i>
                  <span
                    className="small text-secondary"
                    style={{ lineHeight: "1.6" }}
                  >
                    <strong>50% Corporate Reinvestment:</strong> Directed into
                    micro-filtered automated plants, structural package designs,
                    and logistical updates.
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= VISION & MISSION CONTAINER ================= */}
      <section className="container my-5 position-relative z-1">
        <div className="row g-4 text-white">
          <motion.div
            className="col-md-6"
            initial={{ opacity: 0, x: -15 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div
              className="card h-100 border-0 p-4 p-xl-5 position-relative overflow-hidden shadow-sm"
              style={{
                backgroundColor: "var(--brand-deep-blue)",
                borderRadius: "24px",
              }}
            >
              <div className="card-body p-0">
                <h3 className="fw-bold text-white mb-3 pb-2 border-bottom border-white border-opacity-10 fs-4">
                  Our Mission
                </h3>
                <p
                  className="text-white-50 small mb-0"
                  style={{ lineHeight: "1.7" }}
                >
                  To systematically alleviate local safe water scarcity hurdles
                  through direct consumer crowd-support alternatives,
                  transforming automated mass bottling lines into active assets
                  for community micro-infrastructure empowerment.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="col-md-6"
            initial={{ opacity: 0, x: 15 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div
              className="card h-100 border-0 p-4 p-xl-5 position-relative overflow-hidden shadow-sm"
              style={{
                backgroundColor: "var(--brand-charcoal)",
                borderRadius: "24px",
              }}
            >
              <div className="card-body p-0">
                <h3
                  className="fw-bold mb-3 pb-2 border-bottom border-white border-opacity-10 fs-4"
                  style={{ color: "var(--brand-mineral-aqua)" }}
                >
                  Our Vision
                </h3>
                <p
                  className="text-white-50 small mb-0"
                  style={{ lineHeight: "1.7" }}
                >
                  To foster a standard of zero-compromise business ethics where
                  clean, accessible drinking water resources are protected
                  firmly as an undeniable human right, not restricted as
                  high-margin luxury commodities.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= STRATEGIC VALUE PROPOSITION MATRIX ================= */}
      <section
        className="container my-5 py-5 text-center position-relative z-1"
        style={{
          backgroundColor: "rgba(255,255,255,0.5)",
          border: "1px solid rgba(0,0,0,0.03)",
          borderRadius: "28px",
        }}
      >
        <h2
          className="fw-bold mb-2 fs-3"
          style={{ color: "var(--brand-deep-blue)", letterSpacing: "-0.5px" }}
        >
          Engineered for Conscious Value
        </h2>
        <p className="text-muted small px-3 mb-5 max-w-xl mx-auto">
          Aligning uncompromised multi-tier industrial processes alongside
          explicit socio-environmental goals.
        </p>

        <motion.div
          className="row g-4 px-3"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-40px" }}
          variants={{
            hidden: { opacity: 0 },
            show: { opacity: 1, transition: { staggerChildren: 0.1 } },
          }}
        >
          {benefits.map((item, index) => (
            <div className="col-md-6 col-lg-3" key={index}>
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 15 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
                }}
                whileHover={{
                  y: -6,
                  boxShadow: "0px 12px 30px rgba(0, 0, 0, 0.03)",
                }}
                className="card border-0 h-100 bg-white py-4 px-3 align-items-center"
                style={{
                  borderRadius: "20px",
                  border: "1px solid rgba(0,0,0,0.03)",
                  transition: "transform 0.25s ease",
                }}
              >
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                  style={{
                    background: "rgba(0,0,0,0.01)",
                    width: "54px",
                    height: "54px",
                  }}
                >
                  <i
                    className={`bi ${item.icon} fs-4`}
                    style={{ color: item.styleColor }}
                  ></i>
                </div>
                <h6
                  className="fw-bold text-dark mb-2"
                  style={{ fontSize: "0.95rem" }}
                >
                  {item.title}
                </h6>
                <p
                  className="card-text text-muted small mb-0"
                  style={{ lineHeight: "1.5", fontSize: "0.85rem" }}
                >
                  {item.text}
                </p>
              </motion.div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* ================= STEP SYSTEM: 7-STEP PURIFICATION ================= */}
      <section className="container my-5 py-4 position-relative z-1">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-5"
        >
          <span
            className="badge px-3 py-1 mb-2 text-uppercase fw-semibold tracking-wider"
            style={{
              backgroundColor: "rgba(54, 191, 216, 0.1)",
              color: "var(--brand-deep-blue)",
              fontSize: "0.75rem",
              borderRadius: "6px",
            }}
          >
            Rigorous Production Standards
          </span>
          <h2
            className="fw-bold mb-3 fs-3"
            style={{ color: "var(--brand-deep-blue)", letterSpacing: "-0.5px" }}
          >
            7-Step Scientific Purification
          </h2>
          <p
            className="text-muted mx-auto small mb-0"
            style={{ maxWidth: "500px", lineHeight: "1.6" }}
          >
            Advanced multi-tier mechanical protocols engineered cleanly to
            sustain absolute chemical clarity and ecosystem safety parameters.
          </p>
        </motion.div>

        <motion.div
          className="row g-4 justify-content-center"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-30px" }}
          variants={{
            hidden: { opacity: 0 },
            show: { opacity: 1, transition: { staggerChildren: 0.1 } },
          }}
        >
          {purificationSteps.map((process, index) => (
            <div className="col-sm-6 col-md-4 col-lg-3" key={index}>
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0, transition: fluidSpring },
                }}
                whileHover={{
                  y: -8,
                  transition: { duration: 0.3, ease: "easeOut" },
                }}
                className="card h-100 border-0 bg-white position-relative overflow-hidden"
                style={{
                  borderRadius: "20px",
                  border: "1px solid rgba(0,0,0,0.03)",
                }}
              >
                <div
                  className="position-absolute top-0 start-0 w-100"
                  style={{
                    height: "3px",
                    backgroundColor: "var(--brand-mineral-aqua)",
                    opacity: 0.5,
                  }}
                />

                <div className="card-body p-4 d-flex flex-column justify-content-between">
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span
                        className="fw-bold d-flex align-items-center justify-content-center"
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          backgroundColor: "rgba(54, 191, 216, 0.08)",
                          color: "var(--brand-deep-blue)",
                          fontSize: "0.8rem",
                        }}
                      >
                        {process.step}
                      </span>
                      <i className="bi bi-shield-check text-muted opacity-25 fs-5"></i>
                    </div>
                    <h5
                      className="fw-bold mb-3"
                      style={{
                        color: "var(--brand-deep-blue)",
                        fontSize: "0.95rem",
                        lineHeight: "1.4",
                      }}
                    >
                      {process.name}
                    </h5>
                  </div>

                  {/* Masked Image Frame */}
                  <div
                    className="rounded-3 overflow-hidden position-relative mt-2"
                    style={{ height: "130px", backgroundColor: "#fafafa" }}
                  >
                    <LazyLoadImage
                      src={process.imgUrl}
                      alt={`NGO Waters industrial ${process.name}`}
                      className="w-100 h-100"
                      effect="blur"
                      style={{ objectFit: "cover" }}
                    />
                    <div
                      className="position-absolute bottom-0 start-0 w-100 p-2"
                      style={{
                        background:
                          "linear-gradient(to top, rgba(0,0,0,0.3), transparent)",
                      }}
                    >
                      <span
                        className="text-white px-2 py-0.5 rounded backdrop-blur"
                        style={{
                          backgroundColor: "rgba(255,255,255,0.1)",
                          fontSize: "0.6rem",
                          letterSpacing: "0.5px",
                        }}
                      >
                        CERTIFIED
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* ================= HIGH-CONVERSION CTA ================= */}
      <section className="container my-5 position-relative z-1">
        <div
          className="bg-white border p-4 p-md-5 text-center text-md-start"
          style={{
            borderRadius: "24px",
            borderColor: "rgba(0,0,0,0.04)",
            borderLeft: "5px solid var(--brand-copper)",
            boxShadow: "0 8px 30px rgba(0,0,0,0.01)",
          }}
        >
          <div className="row align-items-center g-4">
            <div className="col-md-9">
              <h4
                className="fw-bold mb-2 fs-5"
                style={{ color: "var(--brand-deep-blue)" }}
              >
                Ready to Transition to Conscious Procurement?
              </h4>
              <p
                className="text-muted small mb-0"
                style={{ lineHeight: "1.6" }}
              >
                Whether optimizing corporate bulk allocations, commercial
                storage pipelines, or regional wholesale channels, our
                processing plants are fully geared.
              </p>
            </div>
            <div className="col-md-3 text-md-end">
              <motion.a
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={springConfig}
                href="https://www.cognitoforms.com/HARIOMSINGH1/NGOWatersBulkOrderRequest"
                target="_blank"
                rel="noopener noreferrer"
                className="btn px-4 py-2.5 text-white fw-bold border-0"
                style={{
                  backgroundColor: "var(--brand-deep-blue)",
                  borderRadius: "10px",
                  fontSize: "0.9rem",
                }}
              >
                <i className="bi bi-box-seam-fill me-2"></i> Get Quote
              </motion.a>
            </div>
          </div>
        </div>
      </section>

      <ContactUs />
    </motion.div>
  );
};

export default AboutPage;
